import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, describeError, type StudioUser } from '../api.ts';
import { useRouter } from '../router.tsx';

export function Login() {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [devLink, setDevLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setState('sending');
    setError(null);
    try {
      const result = await api.login(email);
      setDevLink(result.devLink ?? null);
      setState('sent');
    } catch (err) {
      setError(describeError(err));
      setState('idle');
    }
  };

  return (
    <div className="login">
      <form className="login-card" onSubmit={submit}>
        <div className="brand">
          <span className="brand-mark" aria-hidden="true">
            jc
          </span>
          <span>
            Jetscale Coupons <small>Studio</small>
          </span>
        </div>
        {state === 'sent' ? (
          <>
            <h1>Check your inbox</h1>
            <p>If {email} has access, a sign-in link is on its way. It works once and expires in 15 minutes.</p>
            {devLink ? (
              <p className="dev-note">
                Development: email is not sent. The link is in the API log, or{' '}
                <a href={devLink.replace(/^https?:\/\/[^/]+/, '')} data-dev-login>
                  sign in here
                </a>
                .
              </p>
            ) : null}
          </>
        ) : (
          <>
            <h1>Sign in</h1>
            <label htmlFor="email">Work email</label>
            <input id="email" type="email" autoComplete="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
            {error ? (
              <p className="field-error" role="alert">
                {error}
              </p>
            ) : null}
            <button type="submit" className="btn btn-primary" disabled={state === 'sending'}>
              {state === 'sending' ? 'Sending…' : 'Email me a sign-in link'}
            </button>
          </>
        )}
      </form>
    </div>
  );
}

export function VerifyLogin({ onSignedIn }: { onSignedIn: (user: StudioUser) => void }) {
  const { query } = useRouter();
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const token = query.get('token');
    if (!token) {
      setError('This link is incomplete.');
      return;
    }
    api
      .verify(token)
      .then((result) => onSignedIn(result.user))
      .catch((err: unknown) => setError(describeError(err)));
  }, [query, onSignedIn]);

  return (
    <div className="login">
      <div className="login-card">
        {error ? (
          <>
            <h1>Link didn’t work</h1>
            <p role="alert">{error}</p>
            <a className="btn btn-primary" href="/">
              Request a new link
            </a>
          </>
        ) : (
          <p role="status">Signing you in…</p>
        )}
      </div>
    </div>
  );
}
